import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import {
  getRoles,
  getRolePermissions,
  updateRolePermissions,
  getRoleDataScopes,
  updateRoleDataScopes,
} from "../../../api/settings/permissionManager";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Search,
  Filter,
  ShieldCheck,
  Save,
  Loader2,
  BookOpen,
  Database,
  Layers3,
} from "lucide-react";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";

const ACTIONS = ["C", "R", "U", "D", "E", "I", "A"];
const DATA_SCOPE_TYPES = [
  { key: "all", label: "Toàn bộ" },
  { key: "self", label: "Tự tạo" },
  { key: "team", label: "Đội nhóm" },
];

const createEmptyScopeDraft = () => ({
  all: false,
  self: false,
  team: false,
  branches: [],
  departments: [],
});

const buildScopeDrafts = (modules = [], scopes = []) => {
  const drafts = {};

  modules.forEach((moduleItem) => {
    drafts[moduleItem.code] = createEmptyScopeDraft();
  });

  scopes.forEach((scope) => {
    const moduleCode = scope.module_code;
    if (!drafts[moduleCode]) {
      drafts[moduleCode] = createEmptyScopeDraft();
    }

    const type = scope.scope_type === "global" ? "all" : scope.scope_type;
    if (type === "all" || type === "self" || type === "team") {
      drafts[moduleCode][type] = true;
    }
    if (type === "branch" && scope.scope_value) {
      drafts[moduleCode].branches = [
        ...new Set([...drafts[moduleCode].branches, String(scope.scope_value)]),
      ];
    }
    if (type === "department" && scope.scope_value) {
      drafts[moduleCode].departments = [
        ...new Set([
          ...drafts[moduleCode].departments,
          String(scope.scope_value),
        ]),
      ];
    }
  });

  return drafts;
};

const summarizeScope = (draft) => {
  if (!draft) return "Chưa cấu hình";
  if (draft.all) return "Toàn bộ dữ liệu";

  const parts = [];
  if (draft.self) parts.push("Tự tạo");
  if (draft.team) parts.push("Đội nhóm");
  if (draft.branches.length) parts.push(`${draft.branches.length} chi nhánh`);
  if (draft.departments.length) parts.push(`${draft.departments.length} phòng ban`);
  return parts.length ? parts.join(" • ") : "Chưa cấu hình";
};

const PermissionManager = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [scopeModules, setScopeModules] = useState([]);
  const [scopeDrafts, setScopeDrafts] = useState({});
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("permissions");
  const [filter, setFilter] = useState({ keyword: "", module: "" });

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const res = await getRoles();
      setRoles(res.data || []);
    } catch (err) {
      console.error("Lỗi khi tải vai trò", err);
      showErrorToast("Không thể tải danh sách vai trò.");
    }
  };

  const openGuide = () => {
    window.open(
      `${window.location.origin}/guides/settings-role-permissions-guide.html`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const openPermissionDialog = async (role) => {
    setSelectedRole(role);
    setOpen(true);
    setActiveTab("permissions");
    setFilter({ keyword: "", module: "" });
    setLoading(true);

    try {
      const [permissionRes, scopeRes] = await Promise.all([
        getRolePermissions(role.id),
        getRoleDataScopes(role.id),
      ]);

      const scopePayload = scopeRes.data || {};
      setPermissions(permissionRes.data || []);
      setScopeModules(scopePayload.modules || []);
      setScopeDrafts(
        buildScopeDrafts(scopePayload.modules || [], scopePayload.scopes || [])
      );
      setBranches(scopePayload.branches || []);
      setDepartments(scopePayload.departments || []);
    } catch (err) {
      console.error("Lỗi khi tải cấu hình vai trò", err);
      showErrorToast("Không thể tải dữ liệu phân quyền.");
    } finally {
      setLoading(false);
    }
  };

  const closeDialog = () => {
    setOpen(false);
    setSelectedRole(null);
    setPermissions([]);
    setScopeModules([]);
    setScopeDrafts({});
    setBranches([]);
    setDepartments([]);
    setFilter({ keyword: "", module: "" });
  };

  const toggleAction = (code, action) => {
    setPermissions((prev) =>
      prev.map((p) =>
        p.code === code
          ? {
              ...p,
              actions: p.actions.includes(action)
                ? p.actions.filter((a) => a !== action)
                : [...p.actions, action],
            }
          : p
      )
    );
  };

  const handleToggleAllAction = (action) => {
    setPermissions((prev) =>
      prev.map((p) => ({
        ...p,
        actions: p.actions.includes(action)
          ? p.actions.filter((a) => a !== action)
          : [...new Set([...p.actions, action])],
      }))
    );
  };

  const handleToggleAllRow = (code) => {
    setPermissions((prev) =>
      prev.map((p) =>
        p.code === code
          ? {
              ...p,
              actions: p.actions.length === ACTIONS.length ? [] : [...ACTIONS],
            }
          : p
      )
    );
  };

  const updateScopeDraft = (moduleCode, updater) => {
    setScopeDrafts((prev) => {
      const current = prev[moduleCode] || createEmptyScopeDraft();
      return {
        ...prev,
        [moduleCode]: updater(current),
      };
    });
  };

  const toggleScopeFlag = (moduleCode, flag) => {
    updateScopeDraft(moduleCode, (current) => {
      const nextValue = !current[flag];
      if (flag === "all") {
        return nextValue
          ? { ...createEmptyScopeDraft(), all: true }
          : createEmptyScopeDraft();
      }
      return {
        ...current,
        all: false,
        [flag]: nextValue,
      };
    });
  };

  const updateScopeMultiValue = (moduleCode, key, items) => {
    updateScopeDraft(moduleCode, (current) => ({
      ...current,
      all: false,
      [key]: items.map((item) => item.value),
    }));
  };

  const buildScopePayload = () => {
    const payload = [];

    Object.entries(scopeDrafts).forEach(([moduleCode, draft]) => {
      if (draft.all) {
        payload.push({ module_code: moduleCode, scope_type: "all", scope_value: null });
        return;
      }

      if (draft.self) {
        payload.push({ module_code: moduleCode, scope_type: "self", scope_value: null });
      }
      if (draft.team) {
        payload.push({ module_code: moduleCode, scope_type: "team", scope_value: null });
      }
      draft.branches.forEach((value) => {
        payload.push({ module_code: moduleCode, scope_type: "branch", scope_value: value });
      });
      draft.departments.forEach((value) => {
        payload.push({
          module_code: moduleCode,
          scope_type: "department",
          scope_value: value,
        });
      });
    });

    return payload;
  };

  const handleSave = async () => {
    if (!selectedRole) return;

    setSaving(true);
    try {
      await Promise.all([
        updateRolePermissions(selectedRole.id, permissions),
        updateRoleDataScopes(selectedRole.id, buildScopePayload()),
      ]);
      showSuccessToast("Đã lưu cấu hình phân quyền.");
      setOpen(false);
    } catch (err) {
      console.error("Lỗi khi lưu cấu hình vai trò", err);
      showErrorToast("Không thể lưu cấu hình phân quyền.");
    } finally {
      setSaving(false);
    }
  };

  const moduleOptions = useMemo(() => {
    const modules = [
      ...new Set(
        [...permissions, ...scopeModules].map((item) => item.module_id).filter(Boolean)
      ),
    ];
    return modules.sort();
  }, [permissions, scopeModules]);

  const filteredPermissions = useMemo(() => {
    return permissions.filter((p) => {
      const keyword = filter.keyword.trim().toLowerCase();
      const keywordMatch =
        !keyword ||
        p.code.toLowerCase().includes(keyword) ||
        p.name?.toLowerCase().includes(keyword);
      const moduleMatch = !filter.module || p.module_id === filter.module;
      return keywordMatch && moduleMatch;
    });
  }, [permissions, filter]);

  const filteredScopeModules = useMemo(() => {
    return scopeModules.filter((item) => {
      const keyword = filter.keyword.trim().toLowerCase();
      const keywordMatch =
        !keyword ||
        item.code.toLowerCase().includes(keyword) ||
        item.name?.toLowerCase().includes(keyword);
      const moduleMatch = !filter.module || item.module_id === filter.module;
      return keywordMatch && moduleMatch;
    });
  }, [scopeModules, filter]);

  const branchOptions = useMemo(
    () =>
      branches.map((branch) => ({
        value: branch.id,
        label: branch.name,
      })),
    [branches]
  );

  const departmentOptions = useMemo(
    () =>
      departments.map((department) => ({
        value: department.id,
        label: department.branch_name
          ? `${department.branch_name} - ${department.name}`
          : department.name,
      })),
    [departments]
  );

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">
            Phân quyền theo vai trò
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Quản lý quyền chức năng và phạm vi dữ liệu theo từng vai trò trong hệ
            thống.
          </p>
        </div>
        <button
          onClick={openGuide}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-red-200 bg-white text-red-700 hover:bg-red-50 transition-all text-xs font-black uppercase tracking-widest"
        >
          <BookOpen size={16} />
          Hướng dẫn sử dụng
        </button>
      </div>

      <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50/50 border-b border-gray-100">
              <tr>
                <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-widest w-20 text-center">
                  STT
                </th>
                <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-widest w-24">
                  Mã
                </th>
                <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-widest">
                  Tên vai trò
                </th>
                <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-widest">
                  Loại người dùng
                </th>
                <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-widest text-right">
                  Thao tác
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-50">
              {roles.map((role, index) => (
                <tr
                  key={role.id}
                  className="hover:bg-gray-50/50 transition-colors group"
                >
                  <td className="p-5 text-center">
                    <span className="text-xs font-bold text-gray-400">
                      {index + 1}
                    </span>
                  </td>
                  <td className="p-5">
                    <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-lg text-[10px] font-black uppercase tracking-tighter border border-slate-200">
                      #{role.id}
                    </span>
                  </td>
                  <td className="p-5">
                    <p className="font-black text-gray-800 text-sm group-hover:text-red-700 transition-colors">
                      {role.name}
                    </p>
                  </td>
                  <td className="p-5">
                    <span
                      className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase border ${
                        role.userType === "Admin"
                          ? "bg-red-50 text-red-600 border-red-100"
                          : "bg-blue-50 text-blue-600 border-blue-100"
                      }`}
                    >
                      {role.userType}
                    </span>
                  </td>
                  <td className="p-5 text-right">
                    <button
                      onClick={() => openPermissionDialog(role)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl text-xs font-bold hover:bg-red-700 hover:text-white hover:border-red-700 transition-all shadow-sm active:scale-95"
                    >
                      <ShieldCheck size={14} />
                      PHÂN QUYỀN
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
              onClick={closeDialog}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white w-full max-w-7xl max-h-[90vh] rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col"
            >
              <div className="p-8 border-b border-gray-100 flex items-center justify-between bg-white">
                <div className="flex items-center gap-4">
                  <div className="bg-red-700 p-3 rounded-2xl text-white shadow-lg shadow-red-200">
                    <ShieldCheck size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-gray-800 uppercase tracking-tight">
                      Thiết lập <span className="text-red-700">Phân quyền</span>
                    </h3>
                    <p className="text-[11px] text-gray-500 font-bold uppercase tracking-widest mt-1">
                      Vai trò: <span className="text-gray-900">{selectedRole?.name}</span>
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeDialog}
                  className="p-2 hover:bg-gray-100 rounded-full text-gray-400 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="px-8 py-4 border-b border-gray-100 bg-gray-50/40 flex flex-col gap-4">
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setActiveTab("permissions")}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${
                      activeTab === "permissions"
                        ? "bg-red-700 text-white shadow-lg shadow-red-100"
                        : "bg-white text-gray-500 border border-gray-200 hover:border-red-200 hover:text-red-700"
                    }`}
                  >
                    <ShieldCheck size={15} />
                    Quyền chức năng
                  </button>
                  <button
                    onClick={() => setActiveTab("scopes")}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${
                      activeTab === "scopes"
                        ? "bg-red-700 text-white shadow-lg shadow-red-100"
                        : "bg-white text-gray-500 border border-gray-200 hover:border-red-200 hover:text-red-700"
                    }`}
                  >
                    <Database size={15} />
                    Phạm vi dữ liệu
                  </button>
                </div>

                <div className="flex flex-wrap gap-4 items-center">
                  <div className="relative group w-full md:w-72">
                    <Search
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
                      size={18}
                    />
                    <input
                      type="text"
                      placeholder="Tìm kiếm chức năng..."
                      className="w-full bg-white border-2 border-transparent focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none shadow-sm transition-all font-medium text-sm"
                      value={filter.keyword}
                      onChange={(e) =>
                        setFilter((current) => ({
                          ...current,
                          keyword: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="relative group w-full md:w-60">
                    <Filter
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
                      size={18}
                    />
                    <select
                      value={filter.module}
                      onChange={(e) =>
                        setFilter((current) => ({
                          ...current,
                          module: e.target.value,
                        }))
                      }
                      className="w-full bg-white border-2 border-transparent focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none shadow-sm transition-all font-bold text-xs uppercase appearance-none cursor-pointer"
                    >
                      <option value="">Tất cả module</option>
                      {moduleOptions.map((moduleId) => (
                        <option key={moduleId} value={moduleId}>
                          {moduleId}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-auto p-8 custom-scrollbar">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-20 gap-4">
                    <Loader2 className="text-red-700 animate-spin" size={40} />
                    <p className="text-xs font-black text-gray-400 uppercase tracking-[0.2em]">
                      Đang tải dữ liệu phân quyền...
                    </p>
                  </div>
                ) : activeTab === "permissions" ? (
                  <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden">
                    <table className="w-full text-left border-collapse">
                      <thead className="bg-gray-50/80 backdrop-blur-md sticky top-0 z-20 border-b border-gray-100">
                        <tr>
                          <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-widest min-w-[200px]">
                            Chức năng
                          </th>
                          <th className="p-5 text-[11px] font-black text-gray-400 uppercase tracking-widest min-w-[250px]">
                            Mô tả hệ thống
                          </th>
                          {[
                            { code: "C", label: "Tạo" },
                            { code: "R", label: "Xem" },
                            { code: "U", label: "Sửa" },
                            { code: "D", label: "Xóa" },
                            { code: "E", label: "Export" },
                            { code: "I", label: "Import" },
                            { code: "A", label: "Duyệt" },
                          ].map((col) => (
                            <th key={col.code} className="p-5 text-center min-w-[85px]">
                              <div className="flex flex-col items-center gap-2">
                                <span className="text-[10px] font-black text-red-600 uppercase tracking-tighter">
                                  {col.label}
                                </span>
                                <input
                                  type="checkbox"
                                  onChange={() => handleToggleAllAction(col.code)}
                                  className="w-4 h-4 cursor-pointer accent-slate-500"
                                />
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {filteredPermissions.map((permission) => (
                          <tr
                            key={permission.code}
                            className="hover:bg-red-50/30 transition-colors group"
                          >
                            <td className="p-5">
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  checked={permission.actions.length === ACTIONS.length}
                                  onChange={() => handleToggleAllRow(permission.code)}
                                  className="w-4 h-4 cursor-pointer accent-slate-500"
                                />
                                <div>
                                  <span className="text-sm font-black text-gray-800 group-hover:text-red-700 transition-colors block">
                                    {permission.name || permission.code}
                                  </span>
                                  <span className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide">
                                    {permission.code}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="p-5">
                              <p className="text-xs font-medium text-gray-500 italic leading-relaxed line-clamp-2">
                                {permission.description || "Chưa có mô tả cho chức năng này"}
                              </p>
                            </td>
                            {ACTIONS.map((action) => (
                              <td key={action} className="p-5 text-center">
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={permission.actions.includes(action)}
                                    onChange={() => toggleAction(permission.code, action)}
                                    className="w-5 h-5 cursor-pointer accent-slate-500"
                                  />
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-[2rem] border border-blue-100 bg-blue-50/60 p-4 text-sm text-blue-800">
                      <div className="font-black uppercase tracking-widest text-[11px] mb-2">
                        Phạm vi dữ liệu
                      </div>
                      <p>
                        Cấu hình này quyết định người dùng thấy được dữ liệu nào sau khi
                        đã có quyền truy cập chức năng. Nếu chọn <strong>Toàn bộ</strong>,
                        các phạm vi còn lại của cùng chức năng sẽ được bỏ qua.
                      </p>
                    </div>

                    {filteredScopeModules.map((moduleItem) => {
                      const draft = scopeDrafts[moduleItem.code] || createEmptyScopeDraft();
                      const selectedBranches = branchOptions.filter((item) =>
                        draft.branches.includes(item.value)
                      );
                      const selectedDepartments = departmentOptions.filter((item) =>
                        draft.departments.includes(item.value)
                      );

                      return (
                        <div
                          key={moduleItem.code}
                          className="rounded-[2rem] border border-gray-100 bg-white shadow-sm p-5"
                        >
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-black text-gray-800">
                                  {moduleItem.name || moduleItem.code}
                                </span>
                                <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-500 text-[10px] font-black uppercase tracking-widest">
                                  {moduleItem.module_id}
                                </span>
                              </div>
                              <div className="text-[11px] text-gray-400 font-semibold uppercase tracking-wide mt-1">
                                {moduleItem.code}
                              </div>
                              <p className="text-sm text-gray-500 mt-2 max-w-2xl">
                                {moduleItem.description || "Chưa có mô tả cho chức năng này."}
                              </p>
                            </div>

                            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl bg-red-50 text-red-700 border border-red-100 text-xs font-black uppercase tracking-widest">
                              <Layers3 size={14} />
                              {summarizeScope(draft)}
                            </div>
                          </div>

                          <div className="mt-5 grid gap-4 xl:grid-cols-[1.1fr_1fr_1fr]">
                            <div className="rounded-2xl border border-gray-100 p-4 bg-gray-50/40">
                              <div className="text-[11px] font-black uppercase tracking-widest text-gray-400 mb-3">
                                Loại phạm vi
                              </div>
                              <div className="flex flex-wrap gap-3">
                                {DATA_SCOPE_TYPES.map((scopeType) => (
                                  <label
                                    key={scopeType.key}
                                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-bold ${
                                      draft[scopeType.key]
                                        ? "bg-red-50 text-red-700 border-red-200"
                                        : "bg-white text-gray-600 border-gray-200"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={draft[scopeType.key]}
                                      onChange={() =>
                                        toggleScopeFlag(moduleItem.code, scopeType.key)
                                      }
                                      className="w-4 h-4 accent-slate-500"
                                    />
                                    {scopeType.label}
                                  </label>
                                ))}
                              </div>
                            </div>

                            <div className="rounded-2xl border border-gray-100 p-4 bg-gray-50/40">
                              <div className="text-[11px] font-black uppercase tracking-widest text-gray-400 mb-3">
                                Chi nhánh
                              </div>
                              <Select
                                isMulti
                                isDisabled={draft.all}
                                value={selectedBranches}
                                onChange={(items) =>
                                  updateScopeMultiValue(
                                    moduleItem.code,
                                    "branches",
                                    items || []
                                  )
                                }
                                options={branchOptions}
                                styles={customSelectStyles}
                                menuPortalTarget={document.body}
                                placeholder="Chọn chi nhánh..."
                                closeMenuOnSelect={false}
                              />
                            </div>

                            <div className="rounded-2xl border border-gray-100 p-4 bg-gray-50/40">
                              <div className="text-[11px] font-black uppercase tracking-widest text-gray-400 mb-3">
                                Phòng ban
                              </div>
                              <Select
                                isMulti
                                isDisabled={draft.all}
                                value={selectedDepartments}
                                onChange={(items) =>
                                  updateScopeMultiValue(
                                    moduleItem.code,
                                    "departments",
                                    items || []
                                  )
                                }
                                options={departmentOptions}
                                styles={customSelectStyles}
                                menuPortalTarget={document.body}
                                placeholder="Chọn phòng ban..."
                                closeMenuOnSelect={false}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="p-8 border-t border-gray-100 bg-gray-50/30 flex justify-end gap-3 sticky bottom-0">
                <button
                  onClick={closeDialog}
                  className="px-6 py-3 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition-all uppercase text-[10px] tracking-widest"
                >
                  Hủy thay đổi
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-8 py-3 bg-red-700 hover:bg-red-800 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg shadow-red-100 transition-all active:scale-95 uppercase text-[10px] tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      Đang lưu...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Lưu cấu hình
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PermissionManager;
